/**
 * BPO Mutabakat ekranının ortak biçimlendirme yardımcıları.
 * Tüm sayılar Türkçe yerel ayarla yazılır (binlik nokta, ondalık virgül).
 */

const isEmpty = (v) => v === null || v === undefined || v === '';

/**
 * Sayısal değeri güvenli okur. Boş, sayıya çevrilemeyen, NaN ya da Infinity
 * olan her şey null döner.
 *
 * Bu koruma olmadan veri gelmeyen günlerde ekranda "NaN" yazıyordu: servis
 * alanları null döndürüyor, oran hesapları null/null yapıp NaN üretiyor ve
 * NaN "boş" sayılmadığı için biçimlendiriciden geçiyordu.
 */
const toNumber = (v) => {
  if (isEmpty(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Yüzde oranı. Payda yoksa ya da sıfırsa null döner; çağıran taraf "—" yazar.
 * Bölme işlemlerinin tek yerde toplanması NaN'ın geri gelmesini engelliyor.
 */
export const ratio = (part, whole) => {
  const p = toNumber(part);
  const w = toNumber(whole);
  return p === null || w === null || w === 0 ? null : (p / w) * 100;
};

/** 42832 -> "42.832" */
export const formatNumber = (v) => {
  const n = toNumber(v);
  return n === null ? '—' : n.toLocaleString('tr-TR');
};

/** 86.87 -> "86,9" */
export const formatDecimal = (v, decimals = 1) => {
  const n = toNumber(v);
  return n === null
    ? '—'
    : n.toLocaleString('tr-TR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
};

/** 86.87 -> "%86,9" */
export const formatPercent = (v, decimals = 1) =>
  toNumber(v) === null ? '—' : `%${formatDecimal(v, decimals)}`;

/** 249 -> "4:09" */
export const formatDuration = (sec) => {
  const n = toNumber(sec);
  if (n === null) return '—';
  const t = Math.round(n);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

/** 29820 -> "8,3 sa" */
export const formatHours = (sec) => {
  const n = toNumber(sec);
  return n === null ? '—' : `${formatDecimal(n / 3600)} sa`;
};

/** 773.8 -> "12 dk 54 sn" */
export const formatLongDuration = (sec) => {
  const n = toNumber(sec);
  if (n === null) return '—';
  const t = Math.round(n);
  return t < 60 ? `${t} sn` : `${Math.floor(t / 60)} dk ${t % 60} sn`;
};

/**
 * Kuyruk adının son eki hizmeti veren BPO firmasını taşıyor:
 * BEYAZ_ESYA_ASSISTT -> ASSISTT
 */
export const BPO_VENDORS = ['ASSISTT', 'MEGA', 'CMC'];

export const getBpoVendor = (queueName) => {
  if (isEmpty(queueName)) return 'DİĞER';
  const suffix = String(queueName).split('_').pop();
  return BPO_VENDORS.includes(suffix) ? suffix : 'DİĞER';
};

/**
 * Servis seviyesi / oran eşikleri. Tek yerde durması, tablo hücreleriyle
 * grafiklerin aynı renk mantığını paylaşmasını sağlıyor.
 */
export const getLevel = (percent) => {
  const n = toNumber(percent);
  if (n === null) return 'empty';
  if (n >= 80) return 'good';
  if (n >= 70) return 'warn';
  return 'bad';
};

/**
 * Date -> "YYYY-MM-DD", yerel saate göre.
 *
 * toISOString kullanılamaz: UTC'ye çevirdiği için Türkiye'de (UTC+3) gece
 * 00:00–03:00 arasında bir gün geriye kayar ve ekran yanlış günü açardı.
 */
export const toIsoDate = (d) => {
  if (!d || Number.isNaN(new Date(d).getTime())) return null;
  const t = new Date(d);
  const ay = String(t.getMonth() + 1).padStart(2, '0');
  const gun = String(t.getDate()).padStart(2, '0');
  return `${t.getFullYear()}-${ay}-${gun}`;
};

/** Bugünün bir gün öncesi — job'ın işlediği gün. Date döner. */
export const getDefaultDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** "2026-09-03" -> "3 Eylül 2026" */
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const SHORT_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz',
  'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

export const formatDate = (iso) => {
  if (isEmpty(iso)) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
};

/** "2026-09-03" -> "3 Eyl" — grafik ekseni gibi dar yerler için */
export const formatShortDate = (iso) => {
  if (isEmpty(iso)) return '—';
  const [, m, d] = String(iso).slice(0, 10).split('-');
  return `${Number(d)} ${SHORT_MONTHS[Number(m) - 1]}`;
};

/** "2026-09-04T04:18:22" -> "04:18:22" */
export const formatTime = (iso) => {
  if (isEmpty(iso)) return '—';
  const parts = String(iso).split('T');
  return parts.length > 1 ? parts[1].slice(0, 8) : '—';
};

/**
 * Interval dilim etiketinden başlangıç saatini alır.
 * Servis "00:00 - 00:30" biçiminde döndürüyor; ayraç tire de olsa tire-uzun da
 * olsa ilk boşluğa kadarki kısım her zaman başlangıç saatidir.
 */
export const intervalStart = (slot) =>
  isEmpty(slot) ? '' : String(slot).split(' ')[0];
